import { API_ERROR_TYPE, broadcast } from '@/broadcast';
import { useContext } from '@berlin-front/common/composables/context';
import { includes, NonStrict } from '@berlin-front/common/utils';
import { BerlinBffContext } from '@services/BerlinBff';
import { onBeforeUnmount, Ref, unref } from '@vue/composition-api';
import { useMutation } from 'vue-query';
import { ERROR_TYPE } from '@services/ModalParameterContext';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { ServiceDiscriminator } from '@services/service-type-bridge';
import useTraceRoutes from './useTraceRoutes';

type ErrorDetail = { code: string; messages: string[]; params: string[] };

type PublickKeyRef = Ref<string | undefined>;
type PaymentRef = Ref<Record<string | number, unknown> | undefined>;
type TokensRef = Ref<{
  requestToken: string | undefined;
  guestRequestToken: string | undefined;
  npToken: string;
  trustedTrackToken: string | undefined;
}>;
type ErrorCallback = (args: { name: string; message: string; errors: ErrorDetail[] }) => void;

/**
 * ユーザエージェント
 */
// experimentalだけどchromium系は結構ちゃんとしたデータ返すAPIある。
// https://developer.mozilla.org/ja/docs/Web/API/NavigatorUAData
// 使ってないけどより細かいAPIもある。
// https://developer.mozilla.org/ja/docs/Web/API/NavigatorUAData/getHighEntropyValues
const userAgent = (() => {
  if (
    'userAgentData' in window.navigator &&
    // 非セキュアコンテキストではundefinedになるっぽいのでobjectかどうか検証してから使う。
    window.navigator.userAgentData !== null &&
    typeof window.navigator.userAgentData === 'object'
  ) {
    try {
      return JSON.stringify(window.navigator.userAgentData);
    } catch {
      /*  */
    }
  }
  return window.navigator.userAgent;
})();

/**
 * ブロードキャストされるエラーメッセージをハンドルする
 *
 * @param param0
 */
export const useErrorHandle = ({
  activeRef,
  activeService,
  publicKeyRef,
  paymentRef,
  tokensRef,
  errorRouteName,
  errorCallback,
}: {
  /**
   * アクティブで、かつbroadcast messageにserviceが指定されてない場合、ここでメッセージをハンドルする。
   */
  activeRef: Ref<boolean>;
  /**
   * broadcast messageのserviceがここに指定されたserviceと一致する時に、ここでメッセージのハンドルを行う。
   */
  activeService?: ServiceDiscriminator;
  publicKeyRef?: PublickKeyRef;
  paymentRef?: PaymentRef;
  tokensRef?: TokensRef;
  errorRouteName?: string;
  errorCallback: ErrorCallback;
}) => {
  const router = useRouter();
  const traceRoutes = useTraceRoutes();

  const { js } = useContext(BerlinBffContext);
  const { mutate: sendError } = useMutation(
    'atone.jsError',
    (param: NonStrict<Partial<Parameters<typeof js.atoneJavascriptControllerJavascriptErrorPost>[0]>>) =>
      js.atoneJavascriptControllerJavascriptErrorPost(
        {
          // requiredなのでデフォルト値は埋めとく。埋められないやつは空文字。
          name: '',
          message: '',
          stack: '',
          serverResponse: '',
          userAgent,
          httpStatus: '',
          traceRoutes: traceRoutes.value,
          paymentData: JSON.stringify(unref(paymentRef)) ?? '',
          ...param,
        },
        // こいつらはrefで引数渡しとく感じ。
        unref(tokensRef)?.npToken,
        unref(publicKeyRef),
        unref(tokensRef)?.requestToken,
        unref(tokensRef)?.trustedTrackToken,
      ),
    {},
  );

  const unlistenHandlerList: (() => void)[] = [
    broadcast.listen('JS_ERROR', (payload) => {
      if (
        // サービスが一致すること。
        (payload.service &&
          activeService &&
          payload.service.service_type === activeService.service_type &&
          (payload.service.modal_mode ?? '01') === (activeService.modal_mode ?? '01')) ||
        // サービス指定がないならactiveなとき処理する。
        (!payload.service && activeRef.value)
      ) {
        sendError({
          ...activeService,
          name: payload.name,
          message: payload.message,
          stack: payload.stack,
        });
        errorCallback({
          name: payload.name,
          message: [payload.message, payload.stack].filter((v) => v).join('\n'),
          errors: [],
        });
      }
    }),
    broadcast.listen('API_ERROR', (payload) => {
      if (
        // サービスが一致すること。
        (payload.service &&
          activeService &&
          payload.service.service_type === activeService.service_type &&
          (payload.service.modal_mode ?? '01') === (activeService.modal_mode ?? '01')) ||
        // サービス指定がないならactiveなとき処理する。
        (!payload.service && activeRef.value)
      ) {
        const overrideErrObj = {
          ...(payload.name ? { name: payload.name } : {}),
          ...(payload.message ? { message: payload.name } : {}),
        };

        const errObj = {
          ...{
            [API_ERROR_TYPE.SYSTEM_ERROR]: ERROR_TYPE.INTERNAL_EXCEPTION,
            [API_ERROR_TYPE.MAINTAINANCE_ERROR]: ERROR_TYPE.INTERNAL_EXCEPTION,
            [API_ERROR_TYPE.SHOP_ERROR]: ERROR_TYPE.REQUEST_EXCEPTION,
            [API_ERROR_TYPE.BUSINESS_ERROR]: undefined,
          }[payload.errorType],
          ...overrideErrObj,
        };

        // ブロードキャストされたエラーのうち、メンテナンスエラーと業務エラー以外はいい感じにサーバに送信する。
        if (!includes(API_ERROR_TYPE.MAINTAINANCE_ERROR, API_ERROR_TYPE.BUSINESS_ERROR)(payload.errorType)) {
          const httpStatus = (() => {
            try {
              return String(payload.statusCode);
            } catch {
              /* 握りつぶす。 */
            }
            return '';
          })();
          const serverResponse = (() => {
            try {
              JSON.stringify(payload.errorResponse);
            } catch {
              /* 握りつぶす。 */
            }
            return '';
          })();
          sendError({
            ...activeService,
            ...errObj,
            httpStatus,
            serverResponse,
          });
        }
        // エラーコールバックの発火。業務エラー以外。
        if (payload.errorType !== API_ERROR_TYPE.BUSINESS_ERROR) {
          // TODO: errorDetailの精査はBerlinBffに移したい。簡単でしょう？ほんとはサーバ側でやってもらいたいけど。スキーマ変だし。
          const errors: ErrorDetail[] = [];
          const messages = typeof errObj.message === 'string' ? [errObj.message] : [];
          if (payload.errorResponse?.details) {
            if (typeof payload.errorResponse.details === 'string') {
              messages.push(payload.errorResponse.details);
            } else if (Array.isArray(payload.errorResponse.details?.errors)) {
              errors.push(...payload.errorResponse.details.errors);
            }
          }
          errorCallback({
            name: errObj.name ?? '',
            message: messages.join('\n'),
            errors,
          });
        }

        // 画面遷移。
        if (errorRouteName && payload.errorType !== 'BUSINESS_ERROR') {
          // エラータイプ別に画面遷移する。
          // エラー画面はここにある種類で構成される必要がある。
          // `'system' | 'shop' | 'maintainance'` の種別のエラー画面を用意し、パスパラメータで打ち分ける必要がある。
          switch (payload.errorType) {
            case API_ERROR_TYPE.MAINTAINANCE_ERROR: {
              void router.replace({ name: errorRouteName, params: { errorType: 'maintainance' } });
              break;
            }
            case API_ERROR_TYPE.SHOP_ERROR: {
              void router.replace({ name: errorRouteName, params: { errorType: 'shop' } });
              break;
            }
            case API_ERROR_TYPE.SYSTEM_ERROR: {
              void router.replace({ name: errorRouteName, params: { errorType: 'system' } });
              break;
            }
            default: {
              // ケース漏れガード
              ((_type: never) => {})(payload.errorType);
              window.queueMicrotask(() => {
                // マイクロタスク内でerrorをthrowすれば、グローバルエラーハンドラ(window.onerror)に拾われる。
                // そっちでシステムエラー画面にぶっ飛ばされるはず。
                throw new Error(`不正なエラータイプ: ${payload.errorType}`);
              });
            }
          }
        }
      }
    }),
  ];
  onBeforeUnmount(() => {
    unlistenHandlerList.forEach((v) => v());
  });
};
